import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  reloading?: boolean;
}

// A lazy route/section file that failed to download (flaky mobile data, or a
// deploy replaced it) is not a bug in the page: reload once instead of showing
// the error card (same guard as main.tsx, 2026-10-14).
// "reading 'default'": main.tsx cancels vite:preloadError, so a failed lazy()
// import resolves to undefined and React then reads .default of it.
const CHUNK_ERROR = /dynamically imported module|Importing a module script failed|ChunkLoadError|Failed to fetch|error loading dynamically imported module|reading 'default'/i;
const RELOAD_FLAG = 'chunk-reload-attempt';
const isChunkError = (e?: Error) => !!e && CHUNK_ERROR.test(String(e.message || e));

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    let reloading = false;
    if (isChunkError(error)) {
      try {
        reloading = !sessionStorage.getItem(RELOAD_FLAG);
      } catch {
        reloading = false;
      }
    }
    return { hasError: true, error, reloading };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
    if (this.state.reloading) {
      try {
        sessionStorage.setItem(RELOAD_FLAG, '1');
      } catch {
        /* storage blocked — reload anyway, once per page view */
      }
      window.location.reload();
    }
  }

  public render() {
    if (this.state.hasError && this.state.reloading) {
      return <div className="min-h-screen bg-background" aria-busy="true" />;
    }
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <Card className="max-w-md w-full">
            <CardHeader>
              <CardTitle className="text-center text-destructive">
                אירעה שגיאה לא צפויה
              </CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <p className="text-muted-foreground">
                אנחנו מתנצלים על התקלה. אנא רעננו את הדף או נסו שוב מאוחר יותר.
              </p>
              <div className="flex gap-2 justify-center">
                <Button 
                  onClick={() => window.location.reload()}
                  variant="default"
                >
                  רענון הדף
                </Button>
                <Button 
                  onClick={() => window.location.href = '/'}
                  variant="outline"
                >
                  חזרה לדף הבית
                </Button>
              </div>
              {import.meta.env.DEV && this.state.error && (
                <details className="text-left text-xs bg-muted p-2 rounded">
                  <summary>פרטי שגיאה (מצב פיתוח)</summary>
                  <pre className="mt-2">{this.state.error.stack}</pre>
                </details>
              )}
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;